import React, { useEffect, useMemo, useRef } from 'react';
import { ArrowRight01Icon } from 'hugeicons-react';
import { Helmet } from 'react-helmet-async';
import SpaceShowcase from '../Pricing/StudySpacesShowcase';
import QuizShowcase from '../Pricing/QuizShowcase';
import ImageGenShowcase from '../Pricing/ImageGenShowcase';
import { useWindow } from '../../contexts/WindowContext';
import FaqSection from '../Pricing/FaqSection';
import { Faq } from '../../Data/Faqs';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useCheckout } from '../../hooks/useCheckout';
import { getTier } from '../../Data/PricingData';
import { universityLogos } from './universityLogos';
import TestimonialCarousel from './TestimonialCarousel';
import { validateStudentEmail } from './emailValidator';
import useDocumentTitle from '../../UseDocumentTitle';
import '../../Styles/Pricing/Pricing.css';
import '../../Styles/Pricing/PricingCard.css';
import '../../Styles/Student/StudentPage.css';
import ReturnToBuddy from '../Pricing/ReturnToBuddy';

const ULTIMATE_FEATURES = (getTier('ultimate')?.features ?? [])
  .map(f => f.toLowerCase())
  .join(', ');

const faqs: Faq[] = [
  {
    id: 1,
    question: 'How does the student offer work?',
    answer:
      'Students get 12 months completely free on our Ultimate monthly plan. After the free period ends, the subscription continues at the regular monthly rate. You can cancel anytime during the 12-month free period.',
  },
  {
    id: 2,
    question: 'How do I qualify for the student discount?',
    answer:
      'You need a valid .edu email address and must be on the free plan. Click "Claim Your Free Year" and we\'ll check your student email when you sign in.',
  },
  {
    id: 3,
    question: "What's included in the Ultimate plan?",
    answer:
      `Ultimate is our top plan: ${ULTIMATE_FEATURES}.`,
  },
  {
    id: 4,
    question: 'When do payments start?',
    answer:
      "Payments begin automatically after your 12-month free period ends. You'll receive email reminders before this happens, and you can cancel anytime to avoid charges.",
  },
  {
    id: 5,
    question: 'Can I cancel during the free period?',
    answer:
      'Yes! You can cancel anytime during your 12 free months with no penalty. Your access will continue until the end of your free period.',
  },
  {
    id: 6,
    question: "What if I'm already a paid subscriber?",
    answer:
      "This offer is exclusively for free tier users. If you're already on a paid plan, you have access to premium features and don't need this student offer.",
  },
];

const YEAROFFCODE = 'jGn8TI9h';
const CLAIM_PARAM = 'claim';

const STUDENT_OFFER_JSON_LD = {
  '@context': 'http://schema.org',
  '@type': 'EducationalOccupationalProgram',
  name: 'StudyBuddy Student Offer',
  description: 'One year of StudyBuddy Ultimate free for verified students',
  provider: {
    '@type': 'Organization',
    name: 'StudyBuddy',
    url: 'https://studdybuddy.ca',
    sameAs: ['https://cognora.ca'],
  },
  offers: {
    '@type': 'Offer',
    name: 'Student Ultimate - 1 Year Free',
    description: '12 months free access to StudyBuddy Ultimate for students with a .edu email',
    price: '0',
    priceCurrency: 'USD',
    validFrom: '2024-01-01',
    availability: 'http://schema.org/InStock',
    eligibleCustomerType: {
      '@type': 'BusinessEntityType',
      '@id': 'http://purl.org/goodrelations/v1#Student',
    },
  },
  applicationCategory: 'EducationalApplication',
  featureList: [
    'High monthly message limits',
    'Advanced reasoning and problem solving',
    'File upload and analysis',
    'Quiz generation from study materials',
    '24/7 AI tutoring support',
    'Multi-subject academic assistance',
  ],
  educationalCredentialAwarded: 'AI Study Companion Access',
  timeRequired: 'P1Y',
};

const STUDENT_WEBPAGE_JSON_LD = {
  '@context': 'http://schema.org',
  '@type': 'WebPage',
  name: 'StudyBuddy Student Offer',
  description: 'Get 1 year of StudyBuddy Ultimate free - AI study companion for students',
  url: 'https://studdybuddy.ca/student',
  mainEntity: {
    '@type': 'Offer',
    '@id': 'https://studdybuddy.ca/student#offer',
    name: 'StudyBuddy Student Ultimate - 1 Year Free',
    description:
      'Exclusive offer for students: 12 months free access to StudyBuddy Ultimate',
    seller: {
      '@type': 'Organization',
      name: 'StudyBuddy',
    },
    eligibleRegion: 'Worldwide',
    price: '0',
    priceCurrency: 'USD',
  },
};

const StudentPage: React.FC = () => {
  useDocumentTitle('StudyBuddy Student Offer');
  const { windowWidth } = useWindow();
  const { isAuthenticated, isLoading, isDataLoading, isSubscriptionLoading, user, subscription } = useAuth();
  const toast = useToast();
  const { startCheckout, pendingTier } = useCheckout('student_page');
  const resumeHandled = useRef(false);
  const isPending = pendingTier !== null;

  const visibleUniversityLogos = useMemo(() => {
    const count = windowWidth <= 768 ? 4 : 6;
    return universityLogos.slice(0, count);
  }, [windowWidth]);

  const handleClaimYear = () => {
    if (isAuthenticated) {
      if (subscription) {
        toast('This offer is for free-plan accounts. You already have a paid plan.', 'info');
        return;
      }
      if (!user?.email) return;
      const emailValidation = validateStudentEmail(user.email);
      if (!emailValidation.isValid) {
        toast(emailValidation.error || 'Please use a valid student email address ending with .edu', 'error');
        return;
      }
    }

    const returnTo = `${window.location.pathname}?${CLAIM_PARAM}=1`;
    startCheckout({ tier: 'ultimate', interval: 'monthly', currency: 'usd', promoCode: YEAROFFCODE, returnTo });
  };

  // Continue the claim automatically after signing in.
  useEffect(() => {
    if (resumeHandled.current || isLoading || isDataLoading || isSubscriptionLoading) return;
    const params = new URLSearchParams(window.location.search);
    if (!params.has(CLAIM_PARAM)) return;
    resumeHandled.current = true;
    params.delete(CLAIM_PARAM);
    const query = params.toString();
    window.history.replaceState(null, '', window.location.pathname + (query ? `?${query}` : ''));
    if (isAuthenticated) handleClaimYear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isDataLoading, isSubscriptionLoading, isAuthenticated]);

  return (
    <div className="pricing-wrapper">
      <ReturnToBuddy />
      <div className="pricing-container student-page">
        <Helmet>
          <title>StudyBuddy Student Offer - Get 1 Year of Ultimate Free</title>
          <meta
            name="description"
            content="Exclusive student offer: Get 1 full year of StudyBuddy Ultimate completely free! AI-powered study companion for students with .edu email. Start your academic success journey today."
          />
          <meta
            name="keywords"
            content="StudyBuddy student discount, student offer, free year premium, .edu discount, AI study buddy, student AI assistant, academic AI tutor, college study help, university AI tools, student chatbot, free AI for students, study companion, academic success, student premium, AI homework help, study AI, educational AI, student discount AI, college AI assistant, university study tools"
          />
          <meta property="og:title" content="StudyBuddy Student Offer - 1 Year of Ultimate Free" />
          <meta
            property="og:description"
            content="Verified students get 1 full year of StudyBuddy Ultimate free. AI-powered study companion with high message limits, file uploads, Spaces and advanced reasoning."
          />
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://studdybuddy.ca/student" />
          <meta property="og:image" content="https://studdybuddy.ca/student-offer-preview.jpg" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="StudyBuddy Student Offer - 1 Year of Ultimate Free" />
          <meta
            name="twitter:description"
            content="Students get 1 year of Ultimate free: an AI study companion with high limits, Spaces and more."
          />
          <meta name="twitter:image" content="https://studdybuddy.ca/student-offer-preview.jpg" />
          <link rel="canonical" href="https://studdybuddy.ca/student" />
          <meta name="author" content="StudyBuddy by Cognora" />
          <meta name="robots" content="index, follow" />
          <script type="application/ld+json">{JSON.stringify(STUDENT_OFFER_JSON_LD)}</script>
          <script type="application/ld+json">{JSON.stringify(STUDENT_WEBPAGE_JSON_LD)}</script>
        </Helmet>

        <section className="pricing-hero student-offer-hero" aria-labelledby="student-offer-heading">
          <p className="student-offer-badge">Student</p>
          <h1 id="student-offer-heading">
            One Year of Ultimate, <span className="student-offer-highlight">Free</span> for Students
          </h1>
          <p className="student-offer-lede">
            Verified .edu accounts get twelve months on Ultimate at no charge—same AI tools as our paid
            plans. Cancel anytime during the free year.
          </p>
          <button
            type="button"
            onClick={handleClaimYear}
            className="pricing-button ultimate-tier student-offer-cta"
            disabled={isPending}
            aria-busy={isPending}
          >
            <span>{isPending ? 'Opening Checkout…' : 'Get 1 Year Free'}</span>
            <ArrowRight01Icon size={20} aria-hidden />
          </button>
        </section>

        <ImageGenShowcase />
        <QuizShowcase />
        <SpaceShowcase scrollToPricing={() => {}} isExternal />

        <section className="student-testimonial-block" aria-labelledby="student-social-proof-heading">
          <div className="student-testimonial-intro">
            <h2 id="student-social-proof-heading">Loved by Students Everywhere</h2>
            <p>Join thousands of students who use StudyBuddy across courses and campuses.</p>
          </div>
          <TestimonialCarousel />

          <h3 className="student-university-heading">Trusted by Students From Leading Schools</h3>
          <ul className="student-university-logos" aria-label="University logos">
            {visibleUniversityLogos.map((logo) => (
              <li key={logo.src}>
                <img src={logo.src} alt={logo.alt} className="student-university-logo" />
              </li>
            ))}
          </ul>
        </section>

        <FaqSection faqs={faqs} />

        <section className="showcase-cta student-bottom-cta" aria-label="Claim student offer">
          <button
            type="button"
            onClick={handleClaimYear}
            className="pricing-button ultimate-tier student-offer-cta"
            disabled={isPending}
            aria-busy={isPending}
          >
            <span>{isPending ? 'Opening Checkout…' : 'Claim Your Free Year'}</span>
            <ArrowRight01Icon size={20} aria-hidden />
          </button>
        </section>
      </div>
    </div>
  );
};

export default StudentPage;
